import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { fetchJson } from '../utils/api';
import LanguageContext from './LanguageContext';
import config from '../config/env';
import { getBundlePricing } from '../utils/courseBundle';

const CART_STORAGE_KEY = 'course_cart_session';
const API_BASE = `${config.API_BASE_URL}/api/v1/Front`;

const CartContext = createContext();

function loadCartFromStorage() {
  try {
    const raw = sessionStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveCartToStorage(items) {
  try {
    sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch {}
}

function isSameCourse(a, b) {
  return a.courseID === b.courseID && a.courseTitle === b.courseTitle;
}

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState(loadCartFromStorage);
  const { language } = useContext(LanguageContext);
  const cartItemsRef = useRef(cartItems);
  cartItemsRef.current = cartItems;

  useEffect(() => {
    saveCartToStorage(cartItems);
  }, [cartItems]);

  useEffect(() => {
    const items = cartItemsRef.current;
    if (items.length === 0) return;

    const controller = new AbortController();
    const langId = language === 'en' ? 1 : 2;

    Promise.allSettled(
      items.map((item) =>
        fetchJson(`${API_BASE}/Course/By/${item.courseID}/${langId}`, {
          signal: controller.signal,
          cacheKey: `cart-refresh-${item.courseID}-${langId}`,
          cacheTtl: 2 * 60 * 1000,
        })
      )
    ).then((results) => {
      if (controller.signal.aborted) return;
      setCartItems((prev) =>
        prev.map((item, index) => {
          const result = results[index];
          if (result.status === 'fulfilled') {
            const courseData = result.value?.data?.[0];
            if (courseData) {
              return {
                ...item,
                courseTitle: courseData.courseTitle ?? item.courseTitle,
              };
            }
          }
          return item;
        })
      );
    });

    return () => controller.abort();
  }, [language]);

  const addToCart = useCallback((course) => {
    setCartItems((prev) => {
      if (prev.some((item) => isSameCourse(item, course))) {
        return prev;
      }

      const bundlePricing = getBundlePricing(course, course.selectedBundle || 1);

      return [
        ...prev,
        {
          cartKey: `${course.courseID}_${course.courseTitle}_${Date.now()}`,
          courseID: course.courseID,
          courseTitle: course.courseTitle,
          coverImageURL: course.coverImageURL,
          selectedBundle: bundlePricing.selectedBundle,
          costBefore: bundlePricing.originalTotal,
          costAfter: bundlePricing.discountedTotal,
          perLevelPrice: bundlePricing.perLevelPrice,
          originalSinglePrice: bundlePricing.originalSinglePrice,
          bundleDiscountPercent: bundlePricing.discountPercent,
          currencyLabel: course.currencyLabel,
        },
      ];
    });
  }, []);

  const removeFromCart = useCallback((cartKey) => {
    setCartItems((prev) => prev.filter((item) => item.cartKey !== cartKey));
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const isInCart = useCallback(
    (course) => cartItemsRef.current.some((item) => isSameCourse(item, course)),
    []
  );

  const cartCount = cartItems.length;

  return (
    <CartContext.Provider
      value={{ cartItems, cartCount, isInCart, addToCart, removeFromCart, clearCart }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

export default CartContext;
